import { useState } from "react";
import { toast } from "sonner";
import { Smartphone, Landmark, Calculator, Mail, ShieldCheck, Building2, RefreshCw, Plug, Check, FileDown, Info } from "lucide-react";
import { WorkspaceShell } from "@/shell/WorkspaceShell";
import { Card, CardBody, Pill, Button, EmptyState, NarrativeCard, Stat } from "@/design/components";
import { formatRelativeShort } from "@/design/format";
import {
  useConnections,
  useFeedTransactions,
  useSeed,
  useActiveProfile,
  useOrgIndex,
  useMoney,
  useMutate,
  useActorId,
} from "@/domain/hooks";
import { reconcileFeedTxn, importMobileMoneyStatement } from "@/domain/mutations";
import { useConnectionActions } from "@/domain/useMutations";
import type { Connection, ConnectionKind, FeedTransaction } from "@/domain/types";

const KIND: Record<ConnectionKind, { label: string; icon: typeof Smartphone; blurb: string }> = {
  mobile_money: { label: "Mobile money", icon: Smartphone, blurb: "Pull payments as they happen" },
  bank: { label: "Bank feed", icon: Landmark, blurb: "Import the statement automatically" },
  accounting: { label: "Accounting", icon: Calculator, blurb: "Sync recorded transactions" },
  invoice_inbox: { label: "Invoice inbox", icon: Mail, blurb: "Forward invoices to auto-extract" },
  kyc: { label: "KYC provider", icon: ShieldCheck, blurb: "Verify identity and ownership" },
  gov_records: { label: "Government records", icon: Building2, blurb: "Reconcile against procurement" },
};

export function ConnectionsPage({ embedded = false }: { embedded?: boolean }) {
  const profile = useActiveProfile();
  const seed = useSeed();
  const isContractor = profile.group === "contractor";
  const orgId = isContractor ? profile.organisationId : undefined;
  const connections = useConnections(orgId);
  const feed = useFeedTransactions({ orgId, unreconciledOnly: true });
  const orgs = useOrgIndex();
  const isHealth = seed.workspace.id.includes("maternal") || seed.programme.name.toLowerCase().includes("health");

  const inner = (
      <div className={embedded ? "" : "px-4 py-5 md:px-8 md:py-6"}>
        {!embedded && <h1 className="text-2xl font-semibold tracking-tight text-ink">Connections</h1>}
        <p className={embedded ? "text-sm text-mute" : "mt-1 text-sm text-mute"}>
          Impact Ledger reads from the tools you already use, mobile money, accounting, bank and invoices, so spend
          flows in and reconciles itself. You do not re-key it.
        </p>
        {isHealth && (
          <div className="mt-3 rounded-md border border-line bg-subtle/50 px-3 py-2 text-xs text-mute">
            Tracks delivery and equipment only: stock delivered, facilities equipped, training held. Clinical and patient data remain in government systems (DHIS2 / E-Afya, read-only).
          </div>
        )}

        <div className="mt-6 space-y-8">
          <section>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-faint">Sources</h2>
            {connections.length === 0 ? (
              <EmptyState title="No sources yet" body="Connect a mobile-money, bank, accounting or invoice source to bring spend in automatically." />
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {connections.map((c) => (
                  <ConnectionCard key={c.id} c={c} orgName={!isContractor ? orgs[c.organisationId]?.name : undefined} />
                ))}
              </div>
            )}
          </section>

          <FeedSection feed={feed} showOrg={!isContractor} importOrgId={orgId} />
        </div>
      </div>
  );

  return embedded ? inner : <WorkspaceShell>{inner}</WorkspaceShell>;
}

function ConnectionCard({ c, orgName }: { c: Connection; orgName?: string }) {
  const meta = KIND[c.kind];
  const { sync, setStatus, isPending } = useConnectionActions();
  const Icon = meta.icon;
  const tone = c.status === "connected" ? "success" : c.status === "error" ? "danger" : "neutral";
  const statusLabel = c.status === "connected" ? "Connected" : c.status === "error" ? "Attention" : "Not connected";

  const isError = c.status === "error";
  return (
    <Card>
      <CardBody compact>
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-subtle text-mute"><Icon className="h-4 w-4" /></span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ink">{c.provider}</p>
              <p className="truncate text-xs text-faint">{meta.label}{orgName ? ` · ${orgName}` : ""}</p>
            </div>
          </div>
          <Pill variant={tone}>{statusLabel}</Pill>
        </div>
        {c.accountRef && <p className="data mt-3 text-xs text-mute">{c.accountRef}</p>}
        <p className={isError ? "mt-2 flex items-start gap-1.5 text-xs text-warn" : "mt-2 flex items-start gap-1.5 text-xs text-mute"}>
          <Info className={isError ? "mt-px h-3.5 w-3.5 shrink-0 text-warn" : "mt-px h-3.5 w-3.5 shrink-0 text-info"} aria-hidden />
          {isError && c.note ? c.note : meta.blurb}
        </p>
        {c.lastSyncAt && c.status === "connected" && (
          <p className="text-meta mt-3 text-faint">Last sync <span className="data normal-case tracking-normal">{formatRelativeShort(c.lastSyncAt)}</span></p>
        )}
        <div className="mt-3 flex gap-1">
          {c.status === "connected" ? (
            <>
              <Button size="sm" variant="ghost" disabled={isPending} onClick={() => { sync(c.id); toast.success(`Synced ${c.provider}`); }}>
                <RefreshCw className="h-3.5 w-3.5" /> Sync
              </Button>
              <Button size="sm" variant="ghost" disabled={isPending} onClick={() => { setStatus(c.id, "not_connected"); toast.message(`Disconnected ${c.provider}`); }}>
                Disconnect
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" disabled={isPending} onClick={() => { setStatus(c.id, "connected"); toast.success(`Connected ${c.provider}`); }}>
              <Plug className="h-3.5 w-3.5" /> Connect
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function FeedSection({ feed, showOrg, importOrgId }: { feed: FeedTransaction[]; showOrg: boolean; importOrgId?: string }) {
  const seed = useSeed();
  const orgs = useOrgIndex();
  const money = useMoney();
  const mutate = useMutate();
  const actorId = useActorId();
  const [matching, setMatching] = useState<string | null>(null);

  function importStatement() {
    if (!importOrgId) return;
    mutate((s) => importMobileMoneyStatement(s, importOrgId, actorId));
    toast.success("Mobile money statement imported · transactions ready to reconcile");
  }

  function importTxn(t: FeedTransaction) {
    mutate((s) => reconcileFeedTxn(s, t.id, actorId, { action: "import" }));
    toast.success("Imported as confirmed, statement-matched spend");
  }
  function matchTxn(t: FeedTransaction, askId: string) {
    mutate((s) => reconcileFeedTxn(s, t.id, actorId, { action: "match", askId }));
    toast.success("Matched to the existing ask · now statement-matched");
    setMatching(null);
  }
  // Candidate asks to match against: same org, not yet statement-matched, not rejected.
  function candidates(t: FeedTransaction) {
    return seed.spendRequests.filter(
      (r) => r.organisationId === t.organisationId && !r.statementMatchRef && r.state !== "rejected",
    );
  }

  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-faint">Money feed to reconcile</h2>
      <Card>
        <CardBody compact className="flex flex-wrap items-center justify-between gap-4">
          <Stat
            label="Unreconciled"
            value={String(feed.length)}
            sub={feed.length === 0 ? "all spend matched" : "payments waiting to import or match"}
            subTone={feed.length === 0 ? "neutral" : "warn"}
            className="border-0 bg-transparent p-0"
          />
          {importOrgId && (
            <Button size="sm" onClick={importStatement}>
              <FileDown className="h-3.5 w-3.5" /> Import mobile money statement
            </Button>
          )}
        </CardBody>
      </Card>
      <div className="mt-3">
      <NarrativeCard
        variant="briefing"
        title="Reconcile, don't re-key"
        body="These payments came straight from the connected mobile-money and bank feeds. Import one as confirmed spend, or match it to an ask someone already raised. Either way it becomes statement-matched, a higher assurance level than self-reported."
      />
      </div>
      <div className="mt-3">
        {feed.length === 0 ? (
          <EmptyState title="Nothing to reconcile" body="When a connected source records a payment, it appears here to import or match." />
        ) : (
          <div className="overflow-hidden rounded-lg border border-line bg-card">
            {feed.map((t) => (
              <div key={t.id} className="border-b border-hairline px-4 py-3 last:border-0">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{t.description}</p>
                    <p className="text-xs text-faint">
                      {t.date} · {t.counterparty}
                      {t.suggestedCategory ? ` · ${t.suggestedCategory}` : ""}
                      {showOrg ? ` · ${orgs[t.organisationId]?.name}` : ""}
                    </p>
                  </div>
                  <span className="text-sm font-medium tabular-nums text-ink">{money(t.amount)}</span>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setMatching(matching === t.id ? null : t.id)}>Match</Button>
                    <Button size="sm" onClick={() => importTxn(t)}><Check className="h-4 w-4" /> Import</Button>
                  </div>
                </div>
                {matching === t.id && (
                  <div className="mt-2 rounded-md border border-line bg-subtle/50 p-2">
                    <p className="mb-1.5 text-xs text-mute">Match to an existing ask in this organisation:</p>
                    {candidates(t).length === 0 ? (
                      <p className="text-xs text-faint">No open asks to match. Import it as new spend instead.</p>
                    ) : (
                      <ul className="space-y-1">
                        {candidates(t).slice(0, 5).map((r) => (
                          <li key={r.id}>
                            <button onClick={() => matchTxn(t, r.id)} className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-card">
                              <span className="truncate text-ink">{r.title}</span>
                              <span className="tabular-nums text-mute">{money(r.amount)}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
